import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { FileTypes } from '@guryou/core/build/entity';
import { Block, DefaultText, FlexRow, FullScreenSpinner, MediumText, openModal, SmallGrayText } from '@guryou/html-components';
import { resetGetFileState, stateIsLoading, useAllProviderCategories, useEffectOnSuccess } from '@guryou/redux';

import WhatsAppIcon from '../assets/img/whatsAppIcon.svg';

import ContactWidget from './components/ContactWidget';
import LanguagePicker from './components/LanguagePicker';
import ProgressComponent from './components/ProgressComponent';
import FormContainer from './containers/FormContainer';
import MainContainer from './containers/MainContainer';
import ChooseProviderType from './steps/ChooseProviderType';
import ClassLoader from './steps/course/ClassLoader';
import CourseFinalDetails from './steps/course/CourseFInalDetails';
import CourseGeneralInformation from './steps/course/CourseGeneralInformation';
import CourseLocation from './steps/course/CourseLocation';
import CourseStaff from './steps/course/CourseStaff';
import ErrorPage from './steps/ErrorPage';
import ProviderGeneralInfo from './steps/ProviderGeneralInfo';
import SelectService from './steps/SelectService';
import ServiceFinalDetails from './steps/service/ServiceFinalDetails';
import ServiceGeneralInformation from './steps/service/ServiceGeneralInformation';
import ServiceLocation from './steps/service/ServiceLocation';
import ServiceStaff from './steps/service/ServiceStaff';
import useIsMobile from './utils/useIsMobile';
import { useOnboardContext } from './context';
import OnboardModals from './modals';

import './components/onboard-components.css';

const Stepper = ({ step, type }) => {
  switch (step) {
    case -1:
      return <ErrorPage isProvider={true} />;
    case 0:
      return <ErrorPage />;
    case 1:
      return <ChooseProviderType />;
    case 2:
      return <ProviderGeneralInfo />;
    case 3:
      return <SelectService />;
    case 4:
      return type === 'service' ? <ServiceGeneralInformation /> : <CourseGeneralInformation />;
    case 5:
      return type === 'service' ? <ServiceStaff /> : <CourseStaff />;
    case 6:
      return type === 'service' ? <ServiceLocation /> : <CourseLocation />;
    case 7:
      return type === 'service' ? <ServiceFinalDetails /> : <CourseFinalDetails />;
    case 8:
      return type === 'service' ? <ServiceFinalDetails /> : <ClassLoader />;
    default:
      return <ChooseProviderType />;
  }
};

const OnboardComponent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { activeStep, progress, serviceType, maxSteps } = useOnboardContext();

  const history = useHistory();

  const categories = useAllProviderCategories();

  useEffectOnSuccess(
    state => state.files.get,
    data => {
      if (data?.type === FileTypes.video) {
        dispatch(openModal('playVideo', { src: data?.signedUrl }, 'Video guide'));
      }
      dispatch(resetGetFileState());
    }
  );

  if (stateIsLoading(categories)) {
    return <FullScreenSpinner />;
  }

  if (activeStep === 3 && isMobile) {
    return (
      <FlexRow height="100vh" xs={12} justifyContent="center" alignItems="center" margin={0} padding={6}>
        <FlexRow justifyContent="center" alignItems="center">
          <MediumText textAlign="center">{t('ONBOARD.mobileForbidTitle')}</MediumText>
          <DefaultText textAlign="center">{t('ONBOARD.mobileForbidSubtitle')}</DefaultText>
        </FlexRow>
      </FlexRow>
    );
  }

  return (
    <>
      <LanguagePicker />
      <FlexRow
        position="fixed"
        bottom="0"
        margin={0}
        zIndex={100}
        paddingBottom={isMobile ? '10px' : '16px'}
        marginLeft={isMobile ? '25px' : '15px'}
        width="160px"
        alignItems="center"
      >
        <ContactWidget
          width={isMobile ? '50px' : '60px'}
          height={isMobile ? '50px' : '60px'}
          link={process.env.REACT_APP_WATSUP_CONTACT}
          image={WhatsAppIcon}
          color="#25D366"
        />
      </FlexRow>

      <Block minHeight="100%" height="100%" maxHeight="100vh" xs={12} overflowY="auto" justifyContent="center" margin={0} padding={10}>
        <MainContainer alignItems="end">
          <FormContainer>
            <ProgressComponent value={progress} variant="determinate" />
          </FormContainer>
          <FlexRow
            lg={5.6}
            md={5.8}
            xs={12}
            margin={0}
            marginRight={2}
            alignItems="end"
            justifyContent="end"
            visibility={activeStep <= 3 || activeStep > maxSteps + 1 ? 'hidden' : 'visible'}
          >
            <SmallGrayText color="#6C757D" textDecoration="underline" cursor="pointer" onClick={() => history.push('/management/providers')}>
              {t('ONBOARD.finishLater')}
            </SmallGrayText>
          </FlexRow>
        </MainContainer>
        <Stepper step={+activeStep} type={serviceType} />
        <OnboardModals />
      </Block>
    </>
  );
};

export default OnboardComponent;
